import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Spin} from 'com/gongxigames/math/core/game/Spin';
import {FlowState} from '@gongxigames/framework/slot/modules/flow/states/FlowState';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow/FlowModuleEvents';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {FlowModule} from '@gongxigames/framework/slot/modules/flow/FlowModule';

export class FlowFreeSpinsState extends FlowState {

    protected setupEvents(): void {
        this.setEventHandler(ReelsEvents.SPIN_ENDED, this.onAllReelsStopped.bind(this));
        this.setEventHandler(PickBonusEvents.STARTED, this.onPickBonusStarted.bind(this));
        this.setEventHandler(FlowModuleEvents.RESPIN_WIN, this.onRespinWon.bind(this));
        this.setEventHandler(GenericPopupEvents.POPUP_CLOSED, this.onPopupClosed.bind(this));
    }

    protected onPopupClosed(type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.onFreespinsIntroFinished();
        }

        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.onFreeSpinsOutroFinished();
        }
    }

    protected restoreFreeSpinsWinningRoundWinPresentation(): void {
        const spin: Spin = this.module.logic.getSessionForFreeSpinsWinningRound();

        this.emit(FlowModuleEvents.WP_RESTORE, spin);
    }

    protected onFreespinsIntroFinished(): void {
        if (this.module.isRestore) {
            this.emit(FlowModuleEvents.CAN_SPIN);
            this.module.isRestore = false;
        }
    }

    protected onAllReelsStopped(): void {
        this.clearEventsQueue();

        switch (this.module.logic.nextAction) {
            case Action.FREESPIN: {
                this.setupFreeGamesSequence();
                break;
            }
            case Action.SPIN: {
                this.setupEndFreeGamesSequence();
                break;
            }
            case Action.PICK: {
                this.emit(FlowModuleEvents.BONUS_GAME_WON);
                this.setupPickBonusSequence();
                break;
            }
            case Action.RESPIN: {
                this.setupRespinBonusSequence();
                break;
            }

            default:
                break;
        }
    }

    protected setupRespinBonusSequence(): void {
        this.emit(FlowModuleEvents.HAS_RESPIN_ON_NEXT_SPIN);

        if (this.isWin()) {
            if (this.isBigWin()) {
                this.setupBigWinBeforeRespin();
            } else {
                this.setupWinBeforeRespin();
            }
        } else {
            this.setupNoWinBeforeRespin();
        }
    }

    protected setupWinBeforeRespin(): void {
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.RESPIN_WIN);
        this.emit(FlowModuleEvents.WON);
    }

    protected setupBigWinBeforeRespin(): void {
        this.emit(FlowModuleEvents.WON);
        this.emit(FlowModuleEvents.WON_BIG_WIN);
        this.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.RESPIN_WIN);
    }

    protected setupEndFreeGamesSequence(): void {
        this.emit(ReelsEvents.DISABLE_SPIN_BY_SPIN_BUTTON);

        if (this.isWin()) {
            if (this.isBigWin()) {
                this.setupBigWinAfterOnFreeSpinsEnd();
            } else {
                this.setupWinInFreeSpinsEnd();
            }
        } else {
            this.setupNoWinOnFreeSpinsEnd();
        }
    }

    protected setupFreeGamesSequence(): void {
        if (this.module.logic.isRetriggerWon()) {
            this.setupRetriggerSequence();
        } else {
            this.setupCommonSequence();
        }
    }

    protected setupRetriggerSequence(): void {
        const fsWon: number = this.module.logic.getRetriggerCount();

        if (this.isWin()) {
            if (this.isBigWin()) {
                this.setupRetriggerWithBigWin(fsWon);
            } else {
                this.setupRetriggerWithWin(fsWon);
            }
        }
    }

    protected setupCommonSequence(): void {
        if (this.isWin()) {
            if (this.isBigWin()) {
                this.setupBigWin();
            } else {
                this.setupWin();
            }
        } else {
            this.setupNoWin();
        }
    }

    protected setupNoWinBeforeRespin(): void {
        this.emit(FlowModuleEvents.NO_WIN);
        this.emit(FlowModuleEvents.RESPIN_WIN);
    }

    protected setupNoWinOnFreeSpinsEnd(): void {
        this.emit(FlowModuleEvents.NO_WIN);
        this.emit(FlowModuleEvents.CAN_SPIN);
        this.emit(FlowModuleEvents.FREE_SPINS_FINISHED);
    }

    protected setupBigWin(): void {
        this.emit(FlowModuleEvents.WON);
        this.emit(FlowModuleEvents.WON_BIG_WIN);
        this.emit(ReelsEvents.DISABLE_SPIN_BY_SPIN_BUTTON);
        this.addEventToWaitingList(BigWinEvents.FINISHED, ReelsEvents.ENABLE_SPIN_BY_SPIN_BUTTON);
        this.addEventToWaitingList(ReelsEvents.ENABLE_SPIN_BY_SPIN_BUTTON, FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_FREESPINS);
        this.addEventToWaitingList(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED, FlowModuleEvents.CAN_SPIN);
        this.addEventToWaitingList(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, FlowModuleEvents.CAN_SPIN);
    }

    protected setupBigWinAfterOnFreeSpinsEnd(): void {
        this.emit(FlowModuleEvents.WON);
        this.emit(FlowModuleEvents.WON_BIG_WIN);
        this.addEventToWaitingList(BigWinEvents.FINISHED, FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_FREESPINS);
        this.addEventToWaitingList(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED, FlowModuleEvents.FREE_SPINS_FINISHED);
        this.addEventToWaitingList(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, FlowModuleEvents.FREE_SPINS_FINISHED);

        // skip
        this.addEventToWaitingList(SpinButtonEvents.SPIN_BUTTON_PRESSED, FlowModuleEvents.FREE_SPINS_FINISHED);
    }

    protected setupWin(): void {
        this.emit(FlowModuleEvents.WON);
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED,
            FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_FREESPINS);
        this.addEventToWaitingList(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED, FlowModuleEvents.CAN_SPIN);
    }

    protected setupWinInFreeSpinsEnd(): void {
        this.emit(FlowModuleEvents.WON);
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED,
            FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_FREESPINS);
        this.addEventToWaitingList(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED, FlowModuleEvents.FREE_SPINS_FINISHED);
        this.addEventToWaitingList(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, FlowModuleEvents.FREE_SPINS_FINISHED);

        // skip
        this.addEventToWaitingList(SpinButtonEvents.SPIN_BUTTON_PRESSED, FlowModuleEvents.FREE_SPINS_FINISHED);
    }

    protected setupRetriggerWithBigWin(fsWon: number): void {
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED,
            FlowModuleEvents.FREESPINS_RETRIGGER_WIN, fsWon);
        this.addEventToWaitingList(GenericPopupEvents.POPUP_CLOSED, FlowModuleEvents.BIG_WIN_AFTER_RETRIGGER_STARTED);
        this.addEventToWaitingList(BigWinEvents.FINISHED,
            FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_AFTER_POPUP_FREESPINS);
        this.addEventToWaitingList(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED, FlowModuleEvents.CAN_SPIN);
        this.addEventToWaitingList(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, FlowModuleEvents.CAN_SPIN);
        this.emit(FlowModuleEvents.WON);
    }

    protected setupRetriggerWithWin(fsWon: number): void {
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED,
            FlowModuleEvents.FREESPINS_RETRIGGER_WIN, fsWon);
        this.addEventToWaitingList(GenericPopupEvents.POPUP_CLOSED, FlowModuleEvents.SHOULD_START_INDIVIDUAL_WIN_AFTER_POPUP_FREESPINS);
        this.addEventToWaitingList(WinPresentationEvents.PAY_ANIMATIONS_AND_WIN_METER_FINISHED, FlowModuleEvents.CAN_SPIN);
        this.addEventToWaitingList(WinPresentationEvents.ALL_PAY_ANIMATIONS_FINISHED, FlowModuleEvents.CAN_SPIN);

        this.emit(FlowModuleEvents.WON);
    }

    protected setupPickBonusSequence(): void {
        if (this.isWin()) {
            this.setupWinBeforePickBonus();
        } else {
            this.emit(FlowModuleEvents.START_PICK_BONUS);
        }
    }

    protected setupWinBeforePickBonus(): void {
        this.emit(FlowModuleEvents.WON);
        this.addEventToWaitingList(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, FlowModuleEvents.START_PICK_BONUS);
    }

    protected onFreeSpinsOutroFinished(): void {
        this.emit(ReelsEvents.ENABLE_SPIN_BY_SPIN_BUTTON);
        this.emit(FlowModuleEvents.CAN_SPIN);

        if (this.isBigWinFromAllFreeSpins()) {
            this.emit(FlowModuleEvents.BIG_WIN_AFTER_FREE_SPINS_STARTED);
            this.addCallbackToWaitingLIst(BigWinEvents.FINISHED, this.onBigWinAfterFreeSpinsFinished.bind(this));
        } else {
            this.gotoBasicState();
        }
    }

    protected onBigWinAfterFreeSpinsFinished(): void {
        this.gotoBasicState();
    }

    protected onPickBonusStarted(): void {
        this.module.gotoState(FlowModule.FREE_GAMES_PICK_STATE);
    }

    protected onRespinWon(): void {
        this.module.gotoState(FlowModule.FREESPINS_RESPIN_STATE);
    }

    protected isBigWinFromAllFreeSpins(): boolean {
        return this.module.logic.isBigTotalWin();
    }

    protected gotoBasicState(): void {
        this.module.gotoState(FlowModule.BASIC_STATE);
        this.restoreFreeSpinsWinningRoundWinPresentation();
    }
}
